import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAdminLang, ROLE_LABELS } from "./adminLang";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const ROLES = ["super_admin", "editor", "sales", "analyst"];

const Users = () => {
  const { lang, tr } = useAdminLang();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/users").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const clean = { ...editing };
    delete clean.id; delete clean.created_at;
    if (!clean.password) delete clean.password;
    if (editing.id) await api.patch(`/admin/users/${editing.id}`, clean);
    else await api.post("/admin/users", clean);
    setEditing(null); load();
  };

  const remove = async (id) => {
    if (!window.confirm(tr("Confirmer la suppression ?", "Confirm deletion?"))) return;
    await api.delete(`/admin/users/${id}`); load();
  };

  const inputCls = "w-full bg-white/5 border border-white/15 rounded-xl px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-[#FF6600]";

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-black text-white">{tr("Utilisateurs", "Users")}</h1>
        <button onClick={() => setEditing({ role: "analyst", is_active: true })} data-testid="user-create-button"
          className="inline-flex items-center gap-2 bg-[#FF6600] hover:bg-[#FF9900] text-[#08090A] text-xs font-black rounded-full px-5 py-2.5 transition-colors">
          <Plus size={14} /> {tr("Créer", "Create")}
        </button>
      </div>
      <div className="mt-6 bg-[#101214] border border-white/10 rounded-2xl divide-y divide-white/5">
        {rows.map((u) => (
          <div key={u.id} className="flex items-center justify-between gap-4 px-5 py-4" data-testid={`user-row-${u.id}`}>
            <div>
              <p className="text-sm font-bold text-white">{u.full_name || u.email}</p>
              <p className="text-xs text-white/40">{u.email} · <span className="text-[#FF9900]">{ROLE_LABELS[lang][u.role]}</span>{!u.is_active && <span className="text-red-400"> · {tr("désactivé", "disabled")}</span>}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(u)} className="p-2 text-white/50 hover:text-[#FF9900]" data-testid={`user-edit-${u.id}`}><Pencil size={16} /></button>
              <button onClick={() => remove(u.id)} className="p-2 text-white/50 hover:text-red-400" data-testid={`user-delete-${u.id}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={() => setEditing(null)}>
          <div className="w-full max-w-md bg-[#101214] border-l border-white/10 h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-black text-white">{editing.id ? tr("Modifier", "Edit") : tr("Créer", "Create")} {tr("un utilisateur", "a user")}</h2>
              <button onClick={() => setEditing(null)} className="text-white/50 hover:text-white p-1"><X size={20} /></button>
            </div>
            <div className="mt-6 space-y-4">
              <div><label className="block text-[11px] font-bold uppercase text-white/45 mb-1.5">{tr("E-mail", "Email")}</label>
                <input value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className={inputCls} disabled={!!editing.id} data-testid="user-email-field" /></div>
              <div><label className="block text-[11px] font-bold uppercase text-white/45 mb-1.5">{tr("Nom complet", "Full name")}</label>
                <input value={editing.full_name || ""} onChange={(e) => setEditing({ ...editing, full_name: e.target.value })} className={inputCls} data-testid="user-name-field" /></div>
              <div><label className="block text-[11px] font-bold uppercase text-white/45 mb-1.5">{tr("Rôle", "Role")}</label>
                <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className={inputCls} data-testid="user-role-select">
                  {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[lang][r]}</option>)}
                </select></div>
              <div><label className="block text-[11px] font-bold uppercase text-white/45 mb-1.5">{tr("Mot de passe", "Password")} {editing.id && tr("(laisser vide = inchangé)", "(leave empty = unchanged)")}</label>
                <input type="password" value={editing.password || ""} onChange={(e) => setEditing({ ...editing, password: e.target.value })} className={inputCls} data-testid="user-password-field" /></div>
              <label className="flex items-center gap-2.5 text-sm text-white/70">
                <input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} className="w-4 h-4 accent-[#FF6600]" data-testid="user-active-toggle" /> {tr("Compte actif", "Active account")}
              </label>
            </div>
            <button onClick={save} data-testid="user-save-button"
              className="mt-8 bg-[#FF6600] hover:bg-[#FF9900] text-[#08090A] text-xs font-black rounded-full px-6 py-3 transition-colors">{tr("Enregistrer", "Save")}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
